import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Compass, PlayCircle, Sparkles } from "lucide-react";

// Accent → Tailwind class map. Keeps every card token in one place.
const ACCENT = {
  pink:     "border-pink-300/25 hover:border-pink-300/60 from-pink-300/[0.08] text-pink-300",
  orange:   "border-orange-300/25 hover:border-orange-300/60 from-orange-300/[0.08] text-orange-300",
  yellow:   "border-yellow-300/25 hover:border-yellow-300/60 from-yellow-300/[0.08] text-yellow-300",
  lime:     "border-lime-300/25 hover:border-lime-300/60 from-lime-300/[0.08] text-lime-300",
  emerald:  "border-emerald-300/25 hover:border-emerald-300/60 from-emerald-300/[0.08] text-emerald-300",
  sky:      "border-sky-300/25 hover:border-sky-300/60 from-sky-300/[0.08] text-sky-300",
  cyan:     "border-cyan-300/25 hover:border-cyan-300/60 from-cyan-300/[0.08] text-cyan-300",
  violet:   "border-violet-300/25 hover:border-violet-300/60 from-violet-300/[0.08] text-violet-300",
  fuchsia:  "border-fuchsia-300/25 hover:border-fuchsia-300/60 from-fuchsia-300/[0.08] text-fuchsia-300",
  rose:     "border-rose-300/25 hover:border-rose-300/60 from-rose-300/[0.08] text-rose-300",
  amber:    "border-amber-300/25 hover:border-amber-300/60 from-amber-300/[0.08] text-amber-300",
  teal:     "border-teal-300/25 hover:border-teal-300/60 from-teal-300/[0.08] text-teal-300",
};

function TopicCard({ topic }) {
  const accent = ACCENT[topic.accent] || ACCENT.pink;
  const [border, hoverBorder, fromColor, textColor] = accent.split(" ");
  const isComingSoon = topic.ready_skills === 0;
  return (
    <Link
      to={`/topics/${topic.slug}`}
      data-testid={`topic-card-${topic.slug}`}
      className={`fade-up lift rounded-lg border bg-[#152040] relative overflow-hidden flex flex-col group transition-colors ${border} ${hoverBorder}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${fromColor} via-transparent to-transparent pointer-events-none`} />
      <div className="relative p-5 flex flex-col h-full">
        <div className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${textColor}`}>
          {topic.grades.length > 0 ? `Classes ${topic.grades.join(", ")}` : "Foundational"}
        </div>
        <div className="font-display text-lg font-semibold text-zinc-100 leading-snug mb-1">
          {topic.title}
        </div>
        <div className="text-xs text-zinc-400 mb-4 min-h-[36px]">{topic.tagline}</div>

        <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
          <div className="text-[11px] font-mono text-zinc-500">
            {topic.unit_count} unit{topic.unit_count === 1 ? "" : "s"}
            {topic.ready_skills > 0 && (
              <>
                <span className="mx-1 text-zinc-700">·</span>
                <span className={textColor}>{topic.ready_skills} practice-ready</span>
              </>
            )}
          </div>
          {isComingSoon ? (
            <div className="text-[10px] uppercase tracking-[0.15em] text-orange-300 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Soon
            </div>
          ) : (
            <ArrowRight className={`w-4 h-4 ${textColor} group-hover:translate-x-0.5 transition-transform`} />
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Topics() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/learn/topics")
      .then((r) => setTopics(r.data.topics || []))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="topics-page">
      <div className="mb-8 fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Topics</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Master one theme at a time
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Every strand of CBSE Maths woven across Classes 6-12. Pick a topic
            and see how it grows year by year — with a topic-scoped mixed
            practice paper waiting at the end.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-pink-300 border border-pink-300/30 rounded-full px-3 py-1 flex items-center gap-1.5">
          <Compass className="w-3 h-3" /> Cross-grade view
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm" data-testid="topics-loading">Loading topics…</div>}

      {!loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="topics-grid">
          {topics.map((t) => <TopicCard key={t.slug} topic={t} />)}
        </div>
      )}

      {/* Callout */}
      <div className="mt-10 rounded-lg border border-white/10 bg-gradient-to-r from-yellow-300/[0.05] via-orange-300/[0.05] to-pink-300/[0.05] p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-300 mb-1">Why cross-grade?</div>
          <div className="text-sm text-zinc-200 max-w-xl">
            Board-year students often forget the roots of a topic. Jumping
            back a class in the same strand — same colour, same theme —
            re-anchors the concept in minutes.
          </div>
        </div>
        <Link
          to="/learn"
          className="text-yellow-300 text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all shrink-0"
          data-testid="topics-cta-learn"
        >
          Or browse class-wise <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
